import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI, coverLetterAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  FileText, ArrowLeft, Globe, Palette, History, Mail, Languages,
  Image, FileDown, FileType, Loader2, Sparkles, LogOut, ChevronDown, User, Camera, Upload,
  Plus, Trash2, X, GripVertical
} from 'lucide-react';

const TEMPLATES = [
  { id: 'template-a', name: 'Navy Sidebar', hasPhoto: true },
  { id: 'template-a-no-photo', name: 'Navy Sidebar (No Photo)', hasPhoto: false },
  { id: 'template-b', name: 'ATS Standard', hasPhoto: true },
  { id: 'template-b-no-photo', name: 'ATS Standard (No Photo)', hasPhoto: false },
  { id: 'template-c', name: 'Modern Minimal', hasPhoto: true },
  { id: 'template-c-no-photo', name: 'Modern Minimal (No Photo)', hasPhoto: false },
];

const Edit = () => {
  const { cvId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const photoInputRef = useRef(null);
  const iframeRef = useRef(null);

  const [cvData, setCvData] = useState(null);
  const [iframeHeight, setIframeHeight] = useState('297mm');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');

  const [instructions, setInstructions] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [showCoverLetter, setShowCoverLetter] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);

  // Cover letter form - single field
  const [coverLetterRequest, setCoverLetterRequest] = useState('');
  const [coverLetterLanguage, setCoverLetterLanguage] = useState('en');
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [generatedLetter, setGeneratedLetter] = useState(null);

  // Custom sections management
  const [showCustomSections, setShowCustomSections] = useState(false);
  const [customSections, setCustomSections] = useState([]);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionItem, setNewSectionItem] = useState('');
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [savingCustomSections, setSavingCustomSections] = useState(false);

  const fetchCV = useCallback(async () => {
    try {
      const data = await cvAPI.get(cvId);
      setCvData(data);
      setPhotoUrl(data.structuredCv?.candidate?.photoUrl || '');
      // Load custom sections from CV data
      setCustomSections(data.structuredCv?.customSections || []);
    } catch (err) {
      console.error('CV fetch error:', err);
      if (err.response?.status === 404) {
        setError('CV not found. It may have been deleted or the link is invalid.');
      } else {
        setError('Failed to load CV. Please try refreshing the page.');
      }
    } finally {
      setLoading(false);
    }
  }, [cvId]);

  const fetchVersions = useCallback(async () => {
    try {
      const data = await cvAPI.getVersions(cvId);
      setVersions(data);
    } catch (err) {
      console.error('Failed to load versions');
    }
  }, [cvId]);

  useEffect(() => {
    fetchCV();
    fetchVersions();
  }, [fetchCV, fetchVersions]);

  // Auto-resize iframe to fit content
  useEffect(() => {
    const handleIframeLoad = () => {
      if (iframeRef.current) {
        try {
          const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
          if (iframeDoc && iframeDoc.body) {
            const height = Math.max(iframeDoc.body.scrollHeight, iframeDoc.documentElement.scrollHeight);
            setIframeHeight(`${height}px`);
          }
        } catch (e) {
          // Cross-origin or other error, use default height
          setIframeHeight('297mm');
        }
      }
    };

    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', handleIframeLoad);
      // Also try to resize after a delay (for dynamic content)
      const timer = setTimeout(handleIframeLoad, 500);
      return () => {
        iframe.removeEventListener('load', handleIframeLoad);
        clearTimeout(timer);
      };
    }
  }, [cvData?.htmlSnapshot]);

  const handleUpdate = async () => {
    if (!instructions.trim()) return;

    setUpdating(true);
    setError('');

    try {
      const result = await cvAPI.update(cvId, { instructions });
      setCvData(result);
      setInstructions('');
      fetchVersions();
    } catch (err) {
      setError('Failed to update CV');
    } finally {
      setUpdating(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    setError('');

    try {
      const result = await cvAPI.uploadPhoto(file);
      setPhotoUrl(result.photoUrl);
      // Auto-save the photo
      const updateResult = await cvAPI.update(cvId, { photoUrl: result.photoUrl });
      setCvData(updateResult);
      setShowPhotoDialog(false);
      fetchVersions();
    } catch (err) {
      setError('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoUpdate = async () => {
    setUpdating(true);
    setError('');

    try {
      const result = await cvAPI.update(cvId, { photoUrl });
      setCvData(result);
      setShowPhotoDialog(false);
      fetchVersions();
    } catch (err) {
      setError('Failed to update photo');
    } finally {
      setUpdating(false);
    }
  };

  const handleTemplateChange = async (templateId) => {
    setUpdating(true);
    try {
      const result = await cvAPI.update(cvId, { templateId });
      setCvData(result);
      fetchVersions();
    } catch (err) {
      setError('Failed to change template');
    } finally {
      setUpdating(false);
    }
  };

  const handleTranslate = async () => {
    const targetLang = cvData.language === 'en' ? 'fr' : 'en';
    setTranslating(true);
    try {
      const result = await cvAPI.translate(cvId, targetLang);
      setCvData(result);
      fetchVersions();
    } catch (err) {
      setError('Failed to translate CV');
    } finally {
      setTranslating(false);
    }
  };

  const handleRestoreVersion = async (versionId) => {
    setUpdating(true);
    try {
      const result = await cvAPI.restoreVersion(cvId, versionId);
      setCvData(result);
      fetchVersions();
      setShowVersions(false);
    } catch (err) {
      setError('Failed to restore version');
    } finally {
      setUpdating(false);
    }
  };

  const handleGenerateCoverLetter = async () => {
    if (!coverLetterRequest.trim()) return;
    
    setGeneratingLetter(true);
    try {
      const result = await coverLetterAPI.generate({
        cvId,
        request: coverLetterRequest,
        language: coverLetterLanguage,
      });
      setGeneratedLetter(result);
    } catch (err) {
      setError('Failed to generate cover letter');
    } finally {
      setGeneratingLetter(false);
    }
  };

  // Custom Sections Management
  const handleAddSection = () => {
    if (!newSectionTitle.trim()) return;
    setCustomSections([...customSections, { title: newSectionTitle.trim(), items: [] }]);
    setNewSectionTitle('');
    setEditingSectionIndex(customSections.length); // Open newly added section for editing
  };

  const handleRemoveSection = (index) => {
    const updated = customSections.filter((_, i) => i !== index);
    setCustomSections(updated);
    if (editingSectionIndex === index) {
      setEditingSectionIndex(null);
    }
  };

  const handleAddItemToSection = (sectionIndex) => {
    if (!newSectionItem.trim()) return;
    const updated = [...customSections];
    updated[sectionIndex].items = [...updated[sectionIndex].items, newSectionItem.trim()];
    setCustomSections(updated);
    setNewSectionItem('');
  };

  const handleRemoveItemFromSection = (sectionIndex, itemIndex) => {
    const updated = [...customSections];
    updated[sectionIndex].items = updated[sectionIndex].items.filter((_, i) => i !== itemIndex);
    setCustomSections(updated);
  };

  const handleSaveCustomSections = async () => {
    setSavingCustomSections(true);
    setError('');
    try {
      // Create updated structuredCv with new custom sections
      const updatedStructuredCv = {
        ...cvData.structuredCv,
        customSections: customSections
      };
      
      const result = await cvAPI.update(cvId, { 
        structuredCv: updatedStructuredCv,
        instructions: '' // No AI enhancement, just save the data
      });
      setCvData(result);
      setShowCustomSections(false);
      fetchVersions();
    } catch (err) {
      setError('Failed to save custom sections');
    } finally {
      setSavingCustomSections(false);
    }
  };

  const handleExport = (type) => {
    let url;
    switch (type) {
      case 'pdf':
        url = cvAPI.exportPDF(cvId);
        break;
      case 'pdf-watermark':
        url = cvAPI.exportPDFWatermark(cvId);
        break;
      case 'png':
        url = cvAPI.exportPNG(cvId);
        break;
      case 'docx':
        url = cvAPI.exportDOCX(cvId);
        break;
      default:
        return;
    }
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!cvData) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-50">
        <p className="text-zinc-500">CV not found</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-zinc-50" data-testid="edit-page">
      {/* Top Bar */}
      <header className="min-h-14 md:h-16 border-b border-zinc-200 bg-white flex flex-wrap items-center justify-between px-3 md:px-6 py-2 md:py-0 shrink-0 z-20 gap-2">
        <div className="flex items-center gap-2 md:gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
            className="px-2"
          >
            <ArrowLeft className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Back to CVs</span>
          </Button>

          <Separator orientation="vertical" className="h-6 hidden md:block" />

          {/* Language */}
          <div className="flex items-center gap-1 md:gap-2">
            <Globe className="w-4 h-4 text-zinc-400" />
            <span className="text-xs md:text-sm font-medium">{cvData.language === 'en' ? 'EN' : 'FR'}</span>
          </div>

          {/* Template Selector */}
          <Select value={cvData.templateId} onValueChange={handleTemplateChange}>
            <SelectTrigger className="w-[120px] md:w-[180px] h-8 md:h-9 text-xs md:text-sm" data-testid="template-selector">
              <Palette className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEMPLATES.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Action buttons - scrollable on mobile */}
        <div className="flex items-center gap-1 md:gap-2 overflow-x-auto pb-1 md:pb-0 w-full md:w-auto justify-end">
          {/* Versions */}
          <Dialog open={showVersions} onOpenChange={setShowVersions}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="versions-btn" className="text-xs md:text-sm h-8 px-2 md:px-3">
                <History className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                <span className="hidden sm:inline">Versions ({cvData.versionsCount})</span>
                <span className="sm:hidden">{cvData.versionsCount}</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Version History</DialogTitle>
                <DialogDescription>Restore a previous version of your CV</DialogDescription>
              </DialogHeader>
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-zinc-50"
                    >
                      <div>
                        <p className="text-sm font-medium">Version {v.versionNumber}</p>
                        <p className="text-xs text-zinc-500">{v.instruction}</p>
                        <p className="text-xs text-zinc-400">{new Date(v.createdAt).toLocaleString()}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestoreVersion(v.id)}
                        data-testid={`restore-version-${v.versionNumber}`}
                      >
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>

          {/* Cover Letter */}
          <Dialog open={showCoverLetter} onOpenChange={setShowCoverLetter}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="create-letter-btn" className="text-xs md:text-sm h-8 px-2 md:px-3">
                <Mail className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                <span className="hidden sm:inline">Create Letter</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Generate Cover Letter</DialogTitle>
                <DialogDescription>Describe what you need in the cover letter</DialogDescription>
              </DialogHeader>
              {generatedLetter ? (
                <div className="space-y-4">
                  <ScrollArea className="h-[300px] p-4 border rounded-lg bg-zinc-50">
                    <pre className="whitespace-pre-wrap text-sm font-sans">{generatedLetter.content}</pre>
                  </ScrollArea>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => window.open(coverLetterAPI.exportPDF(generatedLetter.id), '_blank')}
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.open(coverLetterAPI.exportDOCX(generatedLetter.id), '_blank')}
                    >
                      <FileType className="w-4 h-4 mr-2" />
                      DOCX
                    </Button>
                    <Button variant="ghost" onClick={() => setGeneratedLetter(null)}>
                      Create New
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Your Request</Label>
                    <Textarea
                      placeholder="e.g., Write a cover letter for a Senior Developer position at Google. Emphasize my 5 years of React experience and leadership skills. Keep it professional but enthusiastic."
                      value={coverLetterRequest}
                      onChange={(e) => setCoverLetterRequest(e.target.value)}
                      rows={5}
                      data-testid="cover-letter-request"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select
                      value={coverLetterLanguage}
                      onValueChange={setCoverLetterLanguage}
                    >
                      <SelectTrigger data-testid="cover-language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleGenerateCoverLetter}
                    disabled={generatingLetter || !coverLetterRequest.trim()}
                    className="w-full"
                    data-testid="generate-cover-letter-btn"
                  >
                    {generatingLetter ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Cover Letter
                      </>
                    )}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Custom Sections */}
          <Dialog open={showCustomSections} onOpenChange={setShowCustomSections}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="custom-sections-btn" className="text-xs md:text-sm h-8 px-2 md:px-3">
                <Plus className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                <span className="hidden sm:inline">Custom Sections</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Manage Custom Sections</DialogTitle>
                <DialogDescription>Add custom sections like Achievements, Publications, Volunteer Work, etc.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Add New Section */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Section title (e.g., Achievements)"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddSection()}
                    data-testid="new-section-title-input"
                  />
                  <Button 
                    onClick={handleAddSection} 
                    disabled={!newSectionTitle.trim()}
                    data-testid="add-section-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* List of Custom Sections */}
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-4">
                    {customSections.length === 0 ? (
                      <p className="text-sm text-zinc-500 text-center py-8">
                        No custom sections yet. Add one above.
                      </p>
                    ) : (
                      customSections.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="border rounded-lg p-3 bg-zinc-50">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <GripVertical className="w-4 h-4 text-zinc-400" />
                              <h4 className="font-medium text-sm">{section.title}</h4>
                              <span className="text-xs text-zinc-400">({section.items?.length || 0} items)</span>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingSectionIndex(editingSectionIndex === sectionIndex ? null : sectionIndex)}
                                data-testid={`toggle-section-${sectionIndex}`}
                              >
                                {editingSectionIndex === sectionIndex ? 'Close' : 'Edit'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveSection(sectionIndex)}
                                className="text-red-500 hover:text-red-700"
                                data-testid={`remove-section-${sectionIndex}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Expanded Section Editor */}
                          {editingSectionIndex === sectionIndex && (
                            <div className="space-y-2 mt-2 pl-6">
                              {/* Items List */}
                              {section.items?.map((item, itemIndex) => (
                                <div key={itemIndex} className="flex items-center gap-2 group">
                                  <span className="text-sm flex-1 text-zinc-700">{item}</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveItemFromSection(sectionIndex, itemIndex)}
                                    className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                                    data-testid={`remove-item-${sectionIndex}-${itemIndex}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                              
                              {/* Add Item Input */}
                              <div className="flex gap-2 mt-2">
                                <Input
                                  placeholder="Add item..."
                                  value={newSectionItem}
                                  onChange={(e) => setNewSectionItem(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleAddItemToSection(sectionIndex)}
                                  className="text-sm"
                                  data-testid={`add-item-input-${sectionIndex}`}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleAddItemToSection(sectionIndex)}
                                  disabled={!newSectionItem.trim()}
                                  data-testid={`add-item-btn-${sectionIndex}`}
                                >
                                  Add
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>

                {/* Save Button */}
                <Button
                  onClick={handleSaveCustomSections}
                  disabled={savingCustomSections}
                  className="w-full"
                  data-testid="save-custom-sections-btn"
                >
                  {savingCustomSections ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Custom Sections'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Right Actions - moved into the same flex container on mobile */}
        <div className="flex items-center gap-1 md:gap-2">
          {/* Translation Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleTranslate}
            disabled={translating}
            data-testid="translate-btn"
            className="text-xs md:text-sm h-8 px-2 md:px-3"
          >
            {translating ? (
              <Loader2 className="w-3 h-3 md:w-4 md:h-4 md:mr-2 animate-spin" />
            ) : (
              <Languages className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
            )}
            <span className="hidden sm:inline">To {cvData.language === 'en' ? 'French' : 'English'}</span>
            <span className="sm:hidden">{cvData.language === 'en' ? 'FR' : 'EN'}</span>
          </Button>

          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-dropdown" className="text-xs md:text-sm h-8 px-2 md:px-3">
                <FileDown className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                <span className="hidden sm:inline">Export</span>
                <ChevronDown className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('pdf-watermark')} data-testid="export-pdf-watermark">
                <FileText className="w-4 h-4 mr-2" />
                PDF with MOCV Watermark
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf">
                <FileText className="w-4 h-4 mr-2" />
                PDF (Clean)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('png')} data-testid="export-png">
                <Image className="w-4 h-4 mr-2" />
                PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('docx')} data-testid="export-docx">
                <FileType className="w-4 h-4 mr-2" />
                DOCX
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Separator orientation="vertical" className="h-6 mx-2" />

          <span className="text-xs md:text-sm text-zinc-500 hidden sm:inline">{user?.name}</span>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn-edit" className="px-2">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Workspace - Stack on mobile, side by side on desktop */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left Panel - Full width on mobile, sidebar on desktop */}
        <aside className="w-full lg:w-[400px] lg:border-r border-b lg:border-b-0 border-zinc-200 bg-white overflow-y-auto p-4 md:p-6 max-h-[40vh] lg:max-h-none">
          <div className="space-y-4 md:space-y-6">
            {/* Candidate Name & Photo */}
            <div className="space-y-4">
              <Label className="text-xs font-medium uppercase tracking-wider text-zinc-500">
                Candidate
              </Label>
              <div className="flex items-center gap-4">
                {/* Photo */}
                <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
                  <DialogTrigger asChild>
                    <button 
                      className="w-16 h-16 rounded-full bg-zinc-100 border-2 border-dashed border-zinc-300 flex items-center justify-center overflow-hidden hover:border-zinc-400 transition-colors relative group"
                      data-testid="photo-edit-btn"
                    >
                      {cvData.structuredCv?.candidate?.photoUrl ? (
                        <>
                          <img 
                            src={cvData.structuredCv.candidate.photoUrl} 
                            alt="Candidate" 
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Camera className="w-5 h-5 text-white" />
                          </div>
                        </>
                      ) : (
                        <User className="w-6 h-6 text-zinc-400" />
                      )}
                    </button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Update Photo</DialogTitle>
                      <DialogDescription>Upload a photo or enter a URL</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* File Upload */}
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        data-testid="photo-file-input-edit"
                      />
                      <button
                        onClick={() => photoInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="w-full border-2 border-dashed border-zinc-200 rounded-lg p-6 text-center hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
                        data-testid="photo-upload-btn-edit"
                      >
                        {uploadingPhoto ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-8 h-8 text-zinc-400 animate-spin" />
                            <p className="text-sm text-zinc-500">Uploading...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="w-8 h-8 text-zinc-400" />
                            <p className="text-sm text-zinc-500">
                              Click to upload photo (PNG/JPG)
                            </p>
                          </div>
                        )}
                      </button>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-zinc-400">Or paste URL</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Input
                          placeholder="https://example.com/photo.jpg"
                          value={photoUrl}
                          onChange={(e) => setPhotoUrl(e.target.value)}
                          data-testid="photo-url-input-edit"
                        />
                      </div>
                      {photoUrl && (
                        <div className="flex justify-center">
                          <img 
                            src={photoUrl} 
                            alt="Preview" 
                            className="w-24 h-24 rounded-full object-cover border"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                      <Button
                        onClick={handlePhotoUpdate}
                        disabled={updating || !photoUrl}
                        className="w-full"
                        data-testid="save-photo-btn"
                      >
                        {updating ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : null}
                        Save Photo URL
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <div>
                  <p className="text-xl font-outfit font-semibold text-zinc-900">
                    {cvData.structuredCv.candidate.name}
                  </p>
                  {cvData.structuredCv.candidate.title && (
                    <p className="text-sm text-zinc-500">{cvData.structuredCv.candidate.title}</p>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* AI Enhancement */}
            <div className="space-y-4">
              <Label className="text-xs font-medium uppercase tracking-wider text-zinc-500">
                AI Enhancement
              </Label>
              <Textarea
                placeholder="e.g., Make the summary more impactful, add more technical keywords..."
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={3}
                data-testid="edit-instructions-input"
              />
              <p className="text-xs text-zinc-400">AI enhancement may take 30-60 seconds</p>
              <Button
                onClick={handleUpdate}
                disabled={updating || !instructions.trim()}
                className="w-full"
                data-testid="update-cv-btn"
              >
                {updating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    AI Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Enhance with AI
                  </>
                )}
              </Button>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md" data-testid="edit-error">
                {error}
              </div>
            )}

            <Separator />

            {/* Quick Info - Hidden on mobile to save space */}
            <div className="space-y-3 hidden md:block">
              <Label className="text-xs font-medium uppercase tracking-wider text-zinc-500">
                CV Details
              </Label>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Template</span>
                  <span className="font-medium">{TEMPLATES.find(t => t.id === cvData.templateId)?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Language</span>
                  <span className="font-medium">{cvData.language === 'en' ? 'English' : 'French'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Versions</span>
                  <span className="font-medium">{cvData.versionsCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Last Updated</span>
                  <span className="font-medium">{new Date(cvData.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Preview Panel - Fills remaining space */}
        <main className="flex-1 bg-zinc-100/50 p-2 md:p-4 overflow-auto" data-testid="preview-panel">
          <div
            className="bg-white shadow-paper mx-auto"
            style={{
              width: '794px',
              minHeight: '1123px',
            }}
          >
            <iframe
              ref={iframeRef}
              srcDoc={cvData.htmlSnapshot}
              title="CV Preview"
              className="border-0"
              style={{ 
                width: '794px',
                height: '1123px',
                minHeight: '1123px',
                display: 'block'
              }}
              data-testid="cv-preview-iframe"
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Edit;
